// Global Instructions Rule Applied!
//
// Capital Call Position: per-investor drawdown position (commitment /
// called to date / % drawn / uncalled / paid / outstanding, Issued calls
// only) with fund-level summary tiles. "New capital call" opens the
// existing capital call form with the fund preselected — issuing stays on
// its one proven path.
//
// Reusable in two modes: standalone (fund Select + New call shown) and
// embedded in the Fund form's Capital Calls tab (fundId fixed, the tab
// already owns the New Call action).
//
// Calls are issued in their own tab, so the position re-reads in the
// background when the user comes back to it (useRefreshOnActivate).

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Progress, Row, Select, Space, Spin, Table, Typography } from 'antd'
import { DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useSelector } from 'react-redux'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_CAPITAL_CALLS } from '../../../state_manager/globals.ts'
import { useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import {
  CapitalCallPosition,
  CapitalCallPositionRow
} from '../../../state_manager/data_models/capital_call_position.ts'
import { fetchFundCallPosition } from '../services/capitalCallsService.ts'
import NumberText from '../../../core/components/NumberText.tsx'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import { FundSelect } from '../../../core/components/FundSelect.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { outsideCommitmentHelp } from '../utils/commitmentTerms.ts'

const { Title, Text } = Typography

// call-component labels share the 'Capital contribution' stem; shorten for
// the grouped Called-to-date sub-column headers
const shortTypeLabel = (label: string): string => {
  if (label === 'Capital contribution') return 'Contributions'
  const m = label.match(/^Capital contribution - (.+)$/)
  if (m) return m[1].charAt(0).toUpperCase() + m[1].slice(1)
  return label
}

interface CallPositionViewProps {
  fundId?: string | null
}

export default function CallPositionView({ fundId: fixedFundId = null }: CallPositionViewProps) {
  const { openFormInTab } = useTabManagerContext()

  const funds = useSelector((state: any) => state.globalConfigs?.funds ?? [])
  const currencyText = useNumberDisplay('currency')

  const embedded = fixedFundId != null
  const [selectedFundId, setSelectedFundId] = useState<string | null>(fixedFundId)
  const [position, setPosition] = useState<CapitalCallPosition | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (fixedFundId != null) setSelectedFundId(fixedFundId)
  }, [fixedFundId])


  // silent: a background re-read keeps the current grid on screen (no spinner)
  const loadPosition = useCallback(async (silent = false) => {
    if (!selectedFundId) return
    if (!silent) setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundCallPosition(selectedFundId, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load fund position')
        return
      }
      setPosition(res.data)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [selectedFundId])

  useEffect(() => {
    loadPosition()
  }, [loadPosition])

  const activateRef = useRefreshOnActivate(() => loadPosition(true), { maxAgeMs: 0 })

  const summary = position?.summary ?? null
  const rows = position?.rows ?? []
  const calledTypes = useMemo(() => position?.called_types ?? [], [position])
  // fees / expenses the fund terms keep outside the commitment do not draw
  // it down (spec A3.9 addendum part 4): the % drawn and Uncalled headers
  // say which line types are left out; null = everything counts, no help
  const outsideHelp = outsideCommitmentHelp(
    summary?.commitment_includes_mgmt_fees,
    summary?.commitment_includes_expenses
  )

  const fundName = useMemo(
    () => funds.find((f: any) => f.id === selectedFundId)?.name ?? 'fund',
    [funds, selectedFundId]
  )

  const handleExport = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    const data = rows.map((r) => ({
      Investor: r.investors__name ?? '',
      Commitment: r.fund_commitments__amount ?? 0,
      ...Object.fromEntries(
        calledTypes.map((t) => [shortTypeLabel(t), r.prog__called_by_type?.[t] ?? 0])
      ),
      'Called Total': r.prog__called_to_date ?? 0,
      '% Drawn': r.prog__pct_drawn ?? 0,
      Uncalled: r.prog__uncalled ?? 0,
      Paid: r.prog__paid_amount ?? 0,
      Outstanding: r.prog__outstanding ?? 0
    }))
    const res = await exportJsonToExcel(
      {
        data,
        columns: [
          'Investor',
          'Commitment',
          ...calledTypes.map(shortTypeLabel),
          'Called Total',
          '% Drawn',
          'Uncalled',
          'Paid',
          'Outstanding'
        ],
        filename: `capital_call_position_${fundName}`,
        percentageColumns: ['% Drawn']
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [rows, calledTypes, fundName])

  const handleNewCall = useCallback(() => {
    // The call form pre-scopes to ?fund=<id> on new (same contract as the
    // fund Capital Calls tab's New Capital Call button).
    openFormInTab(TABLE_CAPITAL_CALLS, null, 'New Capital Call', selectedFundId ? { fund: selectedFundId } : undefined)
  }, [openFormInTab, selectedFundId])

  const columns = useMemo(
    () => [
      {
        title: 'Investor',
        dataIndex: 'investors__name',
        key: 'investor',
        fixed: 'left' as const,
        render: (v: string | null) => v ?? '—'
      },
      {
        title: 'Commitment',
        dataIndex: 'fund_commitments__amount',
        key: 'commitment',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      {
        title: 'Called to date',
        key: 'called_group',
        children: [
          // one sub-column per call component that actually occurs in the
          // fund's issued calls, plus the total
          ...calledTypes.map((typeLabel) => ({
            title: shortTypeLabel(typeLabel),
            key: `called_${typeLabel}`,
            align: 'right' as const,
            render: (_: unknown, row: CapitalCallPositionRow) => {
              const v = row.prog__called_by_type?.[typeLabel] ?? 0
              return Math.abs(v) < 0.005 ? <Text type='secondary'>—</Text> : <NumberText value={v} />
            }
          })),
          {
            title: 'Total',
            dataIndex: 'prog__called_to_date',
            key: 'called',
            align: 'right' as const,
            render: (v: number | null) => (
              <NumberText value={v ?? 0} wrap={(n) => <Text strong>{n}</Text>} />
            )
          }
        ]
      },
      {
        title: (
          <span>
            % drawn
            <HelpTip title={outsideHelp} />
          </span>
        ),
        dataIndex: 'prog__pct_drawn',
        key: 'pct_drawn',
        width: 140,
        render: (v: number | null) => (
          <Progress
            percent={Math.round((v ?? 0) * 1000) / 10}
            size="small"
            format={(p) => `${(p ?? 0).toFixed(1)}%`}
          />
        )
      },
      {
        title: (
          <span>
            Uncalled
            <HelpTip title={outsideHelp} />
          </span>
        ),
        dataIndex: 'prog__uncalled',
        key: 'uncalled',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      {
        title: 'Paid',
        dataIndex: 'prog__paid_amount',
        key: 'paid',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      {
        title: 'Outstanding',
        dataIndex: 'prog__outstanding',
        key: 'outstanding',
        align: 'right' as const,
        render: (v: number | null) => {
          const value = v ?? 0
          if (Math.abs(value) < 0.005) return <Text type="secondary">—</Text>
          return <NumberText value={value} wrap={(n) => <Text type="danger">{n}</Text>} />
        }
      }
    ],
    [currencyText, calledTypes, outsideHelp]
  )

  const tiles = useMemo(() => {
    if (!summary) return []
    return [
      { label: 'Committed', value: currencyText(summary.committed ?? 0) },
      {
        label: 'Called to date',
        value: currencyText(summary.called ?? 0),
        sub: `${(((summary.pct_drawn ?? 0) * 1000) / 10).toFixed(1)}% drawn`
      },
      { label: 'Uncalled', value: currencyText(summary.uncalled ?? 0), help: outsideHelp },
      {
        label: 'Outstanding',
        value: currencyText(summary.outstanding ?? 0),
        danger: Math.abs(summary.outstanding ?? 0) >= 0.005
      }
    ]
  }, [summary, currencyText, outsideHelp])

  const content = (
    <div className="flex flex-col gap-3">
      {!embedded && (
        <Card size="small" title="Parameters">
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <div className="mb-1 text-xs text-gray-500">Fund</div>
              <FundSelect
                style={{ width: '100%' }}
                placeholder="Select fund"
                value={selectedFundId}
                onChange={(value) => setSelectedFundId(value)}
              />
            </Col>
          </Row>
        </Card>
      )}

      {summary ? (
        <Row gutter={[12, 12]}>
          {tiles.map((tile) => (
            <Col xs={12} md={6} key={tile.label}>
              <Card size="small">
                <div className="text-xs text-gray-500">
                  {tile.label}
                  <HelpTip title={tile.help} />
                </div>
                <div className="text-lg font-medium">
                  {tile.danger ? <Text type="danger">{tile.value}</Text> : tile.value}
                </div>
                {tile.sub ? <div className="text-xs text-gray-500">{tile.sub}</div> : null}
              </Card>
            </Col>
          ))}
        </Row>
      ) : null}

      <Card size="small" title="Investor position">
        {loading ? (
          <div className="flex justify-center p-6">
            <Spin />
          </div>
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="fund_commitments__id"
            dataSource={rows}
            columns={columns}
            scroll={{ x: 'max-content' }}
            summary={() => {
              if (!summary) return null
              // leaf order: Investor, Commitment, [per-type], Called total,
              // % drawn, Uncalled, Paid, Outstanding
              const cells: React.ReactNode[] = [
                <Text strong key='label'>Total</Text>,
                <NumberText key='committed' value={summary.committed ?? 0} wrap={(n) => <Text strong>{n}</Text>} />,
                ...calledTypes.map((t) => (
                  <Text strong key={`t-${t}`}>
                    <NumberText value={summary.called_by_type?.[t] ?? 0} />
                  </Text>
                )),
                <NumberText key='called' value={summary.called ?? 0} wrap={(n) => <Text strong>{n}</Text>} />,
                <Text strong key='pct'>{`${(((summary.pct_drawn ?? 0) * 1000) / 10).toFixed(1)}%`}</Text>,
                <NumberText key='uncalled' value={summary.uncalled ?? 0} wrap={(n) => <Text strong>{n}</Text>} />,
                <NumberText key='paid' value={summary.paid ?? 0} wrap={(n) => <Text strong>{n}</Text>} />,
                <NumberText key='outstanding' value={summary.outstanding ?? 0} wrap={(n) => <Text strong>{n}</Text>} />
              ]
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    {cells.map((c, idx) => (
                      <Table.Summary.Cell
                        index={idx}
                        key={idx}
                        align={idx === 0 || idx === 2 + calledTypes.length + 1 ? undefined : 'right'}
                      >
                        {c}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }}
          />
        )}
      </Card>
    </div>
  )

  if (embedded) {
    return (
      <div ref={activateRef}>
        <div className="flex items-center justify-end mb-2">
          <Space>
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={handleExport}
              disabled={loading || rows.length === 0}
            >
              Export
            </Button>
            <Button icon={<ReloadOutlined />} size="small" onClick={() => loadPosition()} disabled={loading || !selectedFundId}>
              Refresh
            </Button>
          </Space>
        </div>
        {content}
      </div>
    )
  }

  return (
    <div ref={activateRef} className="h-full overflow-y-auto bg-gray-50 p-3">
      <Card size="small">
        <div className="flex items-center justify-between mb-3">
          <Title level={5} style={{ margin: 0 }}>
            Capital Call Position
          </Title>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={handleExport}
              disabled={loading || rows.length === 0}
            >
              Export
            </Button>
            <Button icon={<ReloadOutlined />} size="small" onClick={() => loadPosition()} disabled={loading || !selectedFundId}>
              Refresh
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              disabled={!selectedFundId}
              onClick={handleNewCall}
            >
              New capital call
            </Button>
          </Space>
        </div>
        {content}
      </Card>
    </div>
  )
}
